
import React, { useState } from 'react';
import { User } from '../types';
import { Button, Input } from './ui';
import { api } from '../services/api';
import { ArrowRight, CheckCircle, MapPin, Building2, User as UserIcon, Play, Plus, Trash2, Phone, AlertCircle } from 'lucide-react';

interface OnboardingProps {
  user: User;
  onComplete: (updatedUser: User) => void;
}

export const Onboarding = ({ user, onComplete }: OnboardingProps) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Step 1: Name & Contact
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone || '');
  const [secondaryPhones, setSecondaryPhones] = useState<string[]>(user.secondaryPhones || []);

  // Step 2: Practice Type
  const [practiceType, setPracticeType] = useState<'solo' | 'clinic' | null>(null);
  const [location, setLocation] = useState(''); // Solo Google Map Link
  const [clinicName, setClinicName] = useState(user.clinicName || '');
  const [branches, setBranches] = useState<{ name: string, location: string }[]>([{ name: 'Main Branch', location: '' }]);

  const handleAddPhone = () => {
    setSecondaryPhones([...secondaryPhones, '']);
  };

  const handleSecondaryPhoneChange = (index: number, value: string) => {
    const newPhones = [...secondaryPhones];
    newPhones[index] = value;
    setSecondaryPhones(newPhones);
  };

  const handleRemovePhone = (index: number) => {
    const newPhones = secondaryPhones.filter((_, i) => i !== index);
    setSecondaryPhones(newPhones);
  };

  const handleNextStep1 = async () => {
    if (!name.trim()) { setErrorMsg("Please confirm your name."); return; }
    if (!phone.trim()) { setErrorMsg("Please provide a primary phone number."); return; }
    
    setLoading(true);
    setErrorMsg('');
    try {
      const cleanSecondary = secondaryPhones.filter(p => p.trim() !== '');
      await api.updateProfile(user.id, { 
          name, 
          phone, 
          secondaryPhones: cleanSecondary 
      });
      // Update local user object for subsequent steps if needed
      user.name = name;
      user.phone = phone;
      user.secondaryPhones = cleanSecondary;
      
      setStep(2);
    } catch (e: any) {
      console.error(e);
      const msg = e.message || "Failed to update profile. Please check your connection.";
      setErrorMsg(msg.includes('column') ? "System Error: Database columns missing. Please contact admin." : msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBranch = () => {
    setBranches([...branches, { name: `Branch ${branches.length + 1}`, location: '' }]);
  };

  const handleRemoveBranch = (index: number) => {
    setBranches(branches.filter((_, i) => i !== index));
  };

  const handleUpdateBranch = (index: number, field: 'name' | 'location', value: string) => {
    const newBranches = [...branches];
    newBranches[index] = { ...newBranches[index], [field]: value };
    setBranches(newBranches);
  };

  const handleNextStep2 = async () => {
    if (!practiceType) { setErrorMsg("Please select a practice type."); return; }
    setLoading(true);
    setErrorMsg('');
    
    try {
        if (practiceType === 'solo') {
            if (!location.trim()) { setErrorMsg("Please provide a Google Maps location link."); setLoading(false); return; }
            await api.updateProfile(user.id, { location, clinicName: 'Private Clinic' });
        } else {
            if (!clinicName.trim()) { setErrorMsg("Please enter clinic name."); setLoading(false); return; }
            if (branches.some(b => !b.name || !b.location)) { setErrorMsg("Please fill in all branch details."); setLoading(false); return; }
            
            await api.updateProfile(user.id, { clinicName });
            await api.createBranches(user.id, branches);
        }
        setStep(3);
    } catch (e: any) {
        console.error(e);
        setErrorMsg("Failed to save practice details: " + e.message);
    } finally {
        setLoading(false);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      await api.updateProfile(user.id, { onboardingComplete: true });
      onComplete({ 
          ...user, 
          name, 
          clinicName: practiceType === 'clinic' ? clinicName : 'Private Clinic', 
          onboardingComplete: true,
          phone,
          secondaryPhones: secondaryPhones.filter(p => p.trim() !== '')
      });
    } catch (e: any) {
      console.error(e);
      setErrorMsg("Error finalizing setup: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-zinc-950 flex flex-col items-center justify-center p-6 animate-fade-in overflow-y-auto">
      <div className="w-full max-w-2xl">
        {/* Progress Steps */}
        <div className="flex justify-between mb-12 relative px-4">
           <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-zinc-800 -z-10"></div>
           {[1, 2, 3].map(s => (
              <div key={s} className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${step >= s ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.5)]' : 'bg-zinc-800 text-zinc-500'}`}>
                 {step > s ? <CheckCircle size={18}/> : s}
              </div>
           ))}
        </div>

        {/* Step 1: Welcome & Contact */}
        {step === 1 && (
           <div className="space-y-8 text-center animate-slide-up">
              <div className="w-20 h-20 bg-white rounded-2xl mx-auto flex items-center justify-center text-4xl font-bold text-zinc-900 mb-6">G</div>
              <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">Welcome to Galal Dental Lab</h1>
              <p className="text-zinc-400 text-lg max-w-lg mx-auto">The first dental lab with a unified online platform to track all your cases, invoices, and designs in real-time.</p>
              
              <div className="bg-zinc-900 border border-zinc-800 p-8 rounded-2xl max-w-md mx-auto mt-8 text-left space-y-6">
                 {errorMsg && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl text-sm flex gap-3 items-start animate-fade-in">
                       <AlertCircle size={18} className="shrink-0 mt-0.5"/>
                       <div>{errorMsg}</div>
                    </div>
                 )}

                 <div>
                    <label className="block text-sm font-bold text-zinc-500 uppercase mb-3">Confirm your name</label>
                    <div className="relative">
                        <span className="absolute left-4 top-3.5 text-zinc-500 font-medium">DR.</span>
                        <input 
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-xl py-3 pl-12 pr-4 text-white font-bold outline-none focus:border-blue-500 transition-all"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your Name"
                        />
                    </div>
                 </div>

                 <div>
                    <label className="block text-sm font-bold text-zinc-500 uppercase mb-3">Contact Numbers</label>
                    <div className="space-y-3">
                        <div className="relative">
                            <input 
                                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl py-3 pl-10 pr-4 text-white font-medium outline-none focus:border-blue-500 transition-all placeholder-zinc-600"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="Primary / WhatsApp"
                            />
                            <Phone className="absolute left-3 top-3.5 text-zinc-500" size={16} />
                            <div className="absolute right-3 top-3.5 flex items-center gap-1">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                <span className="text-[10px] text-green-500 font-bold uppercase tracking-wide">Notify</span>
                            </div>
                        </div>
                        
                        {secondaryPhones.map((ph, idx) => (
                            <div key={idx} className="relative flex items-center gap-2">
                                <div className="relative flex-1">
                                    <input 
                                        className="w-full bg-zinc-950 border border-zinc-800 rounded-xl py-3 pl-10 pr-4 text-white font-medium outline-none focus:border-zinc-700 transition-all placeholder-zinc-600"
                                        value={ph}
                                        onChange={(e) => handleSecondaryPhoneChange(idx, e.target.value)}
                                        placeholder="Secondary Phone"
                                    />
                                    <Phone className="absolute left-3 top-3.5 text-zinc-600" size={16} />
                                </div>
                                <button onClick={() => handleRemovePhone(idx)} className="p-3 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-500 hover:text-red-400 hover:border-red-900/50">
                                    <Trash2 size={18}/>
                                </button>
                            </div>
                        ))}

                        <button onClick={handleAddPhone} className="text-xs text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1 mt-2">
                            <Plus size={12}/> Add another number
                        </button>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-2">
                        * Only the primary number will receive WhatsApp notifications.
                    </p>
                 </div>

                 <Button onClick={handleNextStep1} disabled={loading} className="w-full h-12 text-base">
                    {loading ? 'Saving...' : 'Next Step'} <ArrowRight size={18}/>
                 </Button>
              </div>
           </div>
        )}

        {/* Step 2: Practice Info */}
        {step === 2 && (
           <div className="space-y-8 animate-slide-up">
              <div className="text-center">
                 <h2 className="text-3xl font-bold text-white mb-2">Tell us about your practice</h2>
                 <p className="text-zinc-400">This helps us optimize pickup and delivery logistics.</p>
              </div>

              {errorMsg && (
                 <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl text-sm text-center">
                    {errorMsg}
                 </div>
              )}

              {!practiceType ? (
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    <button onClick={() => setPracticeType('solo')} className="bg-zinc-900 border border-zinc-800 p-8 rounded-2xl hover:border-blue-500 hover:bg-blue-900/10 transition-all group text-left">
                       <div className="w-14 h-14 bg-zinc-800 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 text-zinc-400 group-hover:text-white transition-colors">
                          <UserIcon size={24}/>
                       </div>
                       <h3 className="text-xl font-bold text-white mb-2">Solo Doctor</h3>
                       <p className="text-sm text-zinc-500">I operate from a single private clinic location.</p>
                    </button>
                    
                    <button onClick={() => setPracticeType('clinic')} className="bg-zinc-900 border border-zinc-800 p-8 rounded-2xl hover:border-purple-500 hover:bg-purple-900/10 transition-all group text-left">
                       <div className="w-14 h-14 bg-zinc-800 rounded-full flex items-center justify-center mb-4 group-hover:bg-purple-600 text-zinc-400 group-hover:text-white transition-colors">
                          <Building2 size={24}/>
                       </div>
                       <h3 className="text-xl font-bold text-white mb-2">Clinic Owner</h3>
                       <p className="text-sm text-zinc-500">I manage a clinic with multiple branches or partners.</p>
                    </button>
                 </div>
              ) : (
                 <div className="bg-zinc-900 border border-zinc-800 p-8 rounded-2xl max-w-lg mx-auto">
                    <div className="flex justify-between items-center mb-6">
                       <h3 className="font-bold text-white text-lg">{practiceType === 'solo' ? 'Delivery Location' : 'Clinic Details'}</h3>
                       <button onClick={() => setPracticeType(null)} className="text-xs text-zinc-500 hover:text-white underline">Change Type</button>
                    </div>

                    {practiceType === 'solo' && (
                       <div className="space-y-4">
                          <Input 
                             label="Google Maps Link" 
                             placeholder="https://maps.google.com/..." 
                             value={location}
                             onChange={(e: any) => setLocation(e.target.value)}
                          />
                          <p className="text-xs text-zinc-500 flex items-center gap-2">
                             <MapPin size={12}/> Use your clinic's pinned location for couriers.
                          </p>
                       </div>
                    )}

                    {practiceType === 'clinic' && (
                       <div className="space-y-6">
                          <Input 
                             label="Clinic Name" 
                             placeholder="e.g. Smile Care Center" 
                             value={clinicName}
                             onChange={(e: any) => setClinicName(e.target.value)}
                          />
                          
                          <div className="space-y-3">
                             <label className="text-xs font-bold text-zinc-500 uppercase flex justify-between">
                                Branches ({branches.length})
                                <button onClick={handleAddBranch} className="text-blue-400 hover:text-blue-300 flex items-center gap-1"><Plus size={12}/> Add</button>
                             </label>
                             <div className="max-h-60 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
                                {branches.map((branch, idx) => (
                                   <div key={idx} className="bg-black/30 p-3 rounded-lg border border-zinc-800 space-y-2 relative group">
                                      {branches.length > 1 && (
                                         <button onClick={() => handleRemoveBranch(idx)} className="absolute top-2 right-2 text-zinc-600 hover:text-red-400 p-1">
                                            <Trash2 size={14}/>
                                         </button>
                                      )}
                                      <input 
                                         className="w-full bg-transparent text-sm text-white placeholder-zinc-600 outline-none border-b border-zinc-700 focus:border-blue-500 py-1"
                                         placeholder="Branch Name"
                                         value={branch.name}
                                         onChange={(e) => handleUpdateBranch(idx, 'name', e.target.value)}
                                      />
                                      <input 
                                         className="w-full bg-transparent text-sm text-white placeholder-zinc-600 outline-none border-b border-zinc-700 focus:border-blue-500 py-1"
                                         placeholder="Google Maps Link"
                                         value={branch.location}
                                         onChange={(e) => handleUpdateBranch(idx, 'location', e.target.value)}
                                      />
                                   </div>
                                ))}
                             </div>
                          </div>
                       </div>
                    )}

                    <Button onClick={handleNextStep2} disabled={loading} className="w-full mt-8 h-12">
                       {loading ? 'Saving...' : 'Confirm Details'}
                    </Button>
                 </div>
              )}
           </div>
        )}

        {/* Step 3: Tutorial */}
        {step === 3 && (
           <div className="space-y-8 animate-slide-up text-center">
              <div>
                 <h2 className="text-3xl font-bold text-white mb-2">How to create a case</h2>
                 <p className="text-zinc-400">Watch this 1-minute guide to get started immediately.</p>
              </div>

              <div className="aspect-video bg-black rounded-2xl border border-zinc-800 shadow-2xl relative overflow-hidden flex items-center justify-center group cursor-pointer">
                 {/* Placeholder for Video - In production, replace with real <video> or iframe */}
                 <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/20 to-purple-900/20"></div>
                 <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 group-hover:scale-110 transition-transform z-10">
                    <Play size={24} className="ml-1 text-white fill-white" />
                 </div>
                 <p className="absolute bottom-4 text-zinc-400 text-sm">Instructional Video Placeholder</p>
              </div>

              <div className="max-w-md mx-auto">
                 {errorMsg && (
                    <div className="bg-red-500/10 text-red-400 p-2 text-sm rounded mb-4">{errorMsg}</div>
                 )}
                 <Button onClick={handleFinish} disabled={loading} className="w-full h-14 text-lg font-bold bg-white text-black hover:bg-zinc-200">
                    {loading ? 'Finalizing...' : 'Go to Dashboard'}
                 </Button>
              </div>
           </div>
        )}
      </div>
    </div>
  );
};
